/**
 * Testing Checklist Runner Page
 *
 * Step-by-step test execution interface with real-time progress tracking.
 * Integrates test-step-card, inline-issue-report-modal, and testing-progress-sidebar.
 *
 * Module: 3.0 - System Operations (Issues Manager Phase 2)
 * PRD: docs/prds/PRD-ISSUES-MANAGER.md v2.1
 */

import { useState } from "react";
import { useRoute, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Edit, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { TestStepCard } from "@/components/testing/test-step-card";
import { InlineIssueReportModal } from "@/components/testing/inline-issue-report-modal";
import { TestingProgressSidebar } from "@/components/testing/testing-progress-sidebar";
import { ChecklistFormDialog } from "@/components/testing/checklist-form-dialog";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { TEST_RESULT_COLORS } from "@/lib/constants/testing-constants";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { TestingChecklist, TestingChecklistItem, TestStepExecution } from "@shared/schema";

interface ItemCounts {
  total: number;
  automated: number;
  manual: number;
  passedAutomated: number;
  passedManual: number;
  failedAutomated: number;
  failedManual: number;
  pendingAutomated: number;
  pendingManual: number;
  issuesAutomated: number; // ISSUE-090: Count of issues linked to automated test items
  issuesManual: number; // ISSUE-090: Count of issues linked to manual test items
}

interface ChecklistWithItems extends TestingChecklist {
  items: TestingChecklistItem[];
  sprintNumber?: string; // Added by backend join
  itemCounts?: ItemCounts; // Added for unified test checklist filtering
}

export default function ChecklistRunner() {
  const [, params] = useRoute("/project-manager/testing-checklists/:id");
  const [, navigate] = useLocation();
  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<TestingChecklistItem | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [testTypeFilter, setTestTypeFilter] = useState<"all" | "automated" | "manual">("all");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch checklist with items
  const { data: checklist, isLoading } = useQuery<ChecklistWithItems>({
    queryKey: ["/api/testing/checklists", params?.id],
    enabled: !!params?.id,
  });

  // Fetch executions for this checklist
  const { data: executions = [] } = useQuery<TestStepExecution[]>({
    queryKey: [`/api/testing/checklists/${params?.id}/executions`],
    enabled: !!params?.id,
  });

  // Fetch issues linked to this checklist
  const { data: checklistIssues = [] } = useQuery<any[]>({
    queryKey: [`/api/testing/checklists/${params?.id}/issues`],
    enabled: !!params?.id,
  });

  // Execute test step mutation
  const executeStepMutation = useMutation({
    mutationFn: async ({
      itemId,
      data,
    }: {
      itemId: string;
      data: {
        result: string;
        responseData: Record<string, any>;
        notes: string;
      };
    }) => {
      // Send responseData as object (Drizzle handles JSONB conversion)
      return await apiRequest(
        "POST",
        `/api/testing/checklist-items/${itemId}/execute`,
        data
      );
    },
    onSuccess: () => {
      toast({
        title: "Test step executed",
        description: "Result saved successfully",
      });
      // Invalidate checklist to update progress
      queryClient.invalidateQueries({
        queryKey: ["/api/testing/checklists", params?.id],
      });
      // Invalidate executions with correct query key
      queryClient.invalidateQueries({
        queryKey: [`/api/testing/checklists/${params?.id}/executions`],
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to save result",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleExecute = async (
    itemId: string,
    data: {
      result: string;
      responseData: Record<string, any>;
      notes: string;
    }
  ) => {
    await executeStepMutation.mutateAsync({ itemId, data });
  };

  const handleReportIssue = (item: TestingChecklistItem) => {
    setSelectedItem(item);
    setIssueModalOpen(true);
  };

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("DELETE", `/api/testing/checklists/${id}`);
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Checklist deleted",
        description: "Checklist has been deleted successfully.",
      });
      navigate("/project-manager/testing-checklists");
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

  const handleEditSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/testing/checklists", params?.id] });
    setEditDialogOpen(false);
    toast({
      title: "Checklist updated",
      description: "Checklist has been updated successfully.",
    });
  };

  const handleConfirmDelete = () => {
    if (checklist) {
      deleteMutation.mutate(checklist.id);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-screen w-full flex-col">
        <PageHeader
          title="Loading..."
          breadcrumbs={[
            { label: "Home", href: "/" },
            { label: "Test Checklists", href: "/project-manager/testing-checklists" },
          ]}
        />
        <div className="p-8">Loading checklist...</div>
      </div>
    );
  }

  if (!checklist) {
    return (
      <div className="flex h-screen w-full flex-col">
        <PageHeader
          title="Checklist Not Found"
          breadcrumbs={[
            { label: "Home", href: "/" },
            { label: "Test Checklists", href: "/project-manager/testing-checklists" },
          ]}
        />
        <div className="flex-1 overflow-auto">
          <div className="grid grid-cols-[1fr_320px] gap-6 p-6">
            <div className="p-8 text-center text-muted-foreground">
              Checklist not found or has been deleted.
            </div>
            <div className="sticky top-4 h-fit">
              <TestingProgressSidebar
                checklist={{
                  id: params?.id || "",
                  checklistNumber: "N/A",
                  title: "Unknown Checklist",
                  checklistType: "unknown",
                  sprintId: "N/A",
                  createdBy: "",
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                } as any}
                stepsCompleted={0}
                totalSteps={0}
                issuesFound={0}
                passRate={0}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Filter items by test type
  const filteredItems = testTypeFilter === "all"
    ? checklist.items
    : checklist.items.filter((item) => (item as any).testType === testTypeFilter);

  // Group items by category for better organization
  const itemsByCategory = filteredItems.reduce(
    (acc, item) => {
      const category = item.category || "Uncategorized";
      if (!acc[category]) acc[category] = [];
      acc[category].push(item);
      return acc;
    },
    {} as Record<string, TestingChecklistItem[]>
  );

  // Get item counts for filter display
  const itemCounts = checklist.itemCounts || {
    total: checklist.items.length,
    automated: 0,
    manual: checklist.items.length,
    passedAutomated: 0,
    passedManual: 0,
    failedAutomated: 0,
    failedManual: 0,
    pendingAutomated: 0,
    pendingManual: 0,
  };

  // Find execution for each item
  const getExecution = (itemId: string) =>
    executions.find((e) => e.checklistItemId === itemId);

  // Get issues for each item
  const getItemIssues = (itemId: string) =>
    checklistIssues.filter((issue) => issue.checklistItemId === itemId);

  return (
    <div className="flex h-screen w-full flex-col">
      <PageHeader
        title={checklist.title}
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Test Checklists", href: "/project-manager/testing-checklists" },
          { label: checklist.checklistNumber },
        ]}
        actions={
          <div className="flex gap-2 items-center">
            {/* Test Type Filter */}
            <Select value={testTypeFilter} onValueChange={(value: "all" | "automated" | "manual") => setTestTypeFilter(value)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter tests" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  All Tests ({itemCounts.total})
                </SelectItem>
                <SelectItem value="automated">
                  Automated ({itemCounts.automated})
                </SelectItem>
                <SelectItem value="manual">
                  Manual ({itemCounts.manual})
                </SelectItem>
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditDialogOpen(true)}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setDeleteDialogOpen(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        }
      />

      <div className="flex-1 overflow-auto">
        <div className="grid grid-cols-[1fr_320px] gap-6 p-6 max-w-full">
          {/* Main Content - Test Steps */}
          <div className="space-y-4">
            {Object.entries(itemsByCategory).map(([category, items]) => (
              <div key={category}>
                <h2 className="text-lg font-semibold mb-4">{category}</h2>
                <Accordion type="multiple" className="space-y-2">
                  {items.map((item) => {
                    const execution = getExecution(item.id);
                    return (
                      <AccordionItem
                        key={item.id}
                        value={item.id}
                        className="border rounded-lg"
                      >
                        <AccordionTrigger className="px-4 hover:no-underline">
                          <div className="flex items-center justify-between w-full pr-4">
                            <span className="font-medium">
                              {item.testId}{" "}
                              {item.stepNumber && `Step ${item.stepNumber}`}:{" "}
                              {item.testDescription}
                            </span>
                            {execution && execution.result && (
                              <Badge
                                variant="outline"
                                className={cn(
                                  "ml-4 shrink-0",
                                  TEST_RESULT_COLORS[execution.result]
                                )}
                              >
                                {execution.result.toUpperCase()}
                              </Badge>
                            )}
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4">
                          <TestStepCard
                            item={item}
                            execution={execution}
                            linkedIssues={getItemIssues(item.id)}
                            onExecute={handleExecute}
                            onReportIssue={handleReportIssue}
                          />
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </div>
            ))}
          </div>

          {/* Right Sidebar - Testing Progress */}
          <div className="sticky top-4 h-fit">
            <TestingProgressSidebar
              checklist={checklist}
              stepsCompleted={checklist.stepsCompleted || 0}
              totalSteps={checklist.totalSteps || 0}
              issuesFound={checklist.issuesFound || 0}
              passRate={typeof checklist.passRate === 'number' ? checklist.passRate : parseFloat(checklist.passRate || '0')}
            />
          </div>
        </div>
      </div>

      {/* Inline Issue Report Modal */}
      <InlineIssueReportModal
        checklistItem={selectedItem}
        checklist={checklist}
        open={issueModalOpen}
        onOpenChange={setIssueModalOpen}
      />

      {/* Edit Checklist Dialog */}
      <ChecklistFormDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={handleEditSuccess}
        checklist={checklist ? {
          id: checklist.id,
          title: checklist.title,
          sprintId: checklist.sprintId,
          checklistType: checklist.checklistType,
          description: checklist.description,
        } : null}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Checklist?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete checklist "{checklist?.checklistNumber}"?
              This action cannot be undone and will also delete all test steps and executions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
